// The venue's clock.
//
// Spec: CLAUDE.md ("Time on the wire is RFC 3339 in UTC. The venue's timezone decides business
// days, opening hours and time-of-day prices"), spec/06 §3.1 and §4 (windows and bands are local
// times), spec/06 §3.2 (a pass expires "at 23:59:59 venue local time of the last valid day").
//
// Everything here takes the moment as a parameter. Nothing reads a clock: a function that asked
// the machine what time it is would give one answer on the server, another on a station whose
// clock has drifted, and a third in a browser in another timezone — and the three would disagree
// about whether a night pass is still open.
//
// The conversion uses `Intl.DateTimeFormat`, which is in Node, in every browser, in React Native
// (Hermes with ICU) and needs nothing from the network or the filesystem. The C# station uses
// `TimeZoneInfo` over the same IANA names.
import { WEEKDAYS } from '@clavian/contracts';
import { assertIntegerInRange, DomainError } from "../errors.js";
export const SECONDS_PER_MINUTE = 60;
export const SECONDS_PER_DAY = 86_400;
export const MILLIS_PER_SECOND = 1000;
const ISO_UTC = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?Z$/;
const LOCAL_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
const LOCAL_TIME = /^([01]\d|2[0-3]):([0-5]\d)$/;
/**
 * An RFC 3339 timestamp in UTC, as milliseconds since the epoch. Offsets are rejected, exactly as
 * the contract rejects them (`IsoDateTime`): two services must never disagree about what the same
 * instant looks like.
 */
export function epochMillisFromIso(value) {
    if (!ISO_UTC.test(value)) {
        throw new DomainError('invalid-moment', `Expected an RFC 3339 timestamp in UTC ending in Z, got "${value}".`, {
            value,
        });
    }
    const millis = Date.parse(value);
    if (Number.isNaN(millis)) {
        throw new DomainError('invalid-moment', `"${value}" is not a real moment.`, { value });
    }
    return millis;
}
/** Back the other way, always with a `Z` and milliseconds, as the contract writes them. */
export function isoFromEpochMillis(millis) {
    assertIntegerInRange(millis, 'millis', -8.64e15, 8.64e15);
    return new Date(millis).toISOString();
}
/** The instant, seen from the venue: its date, its time of day and its weekday. */
export function venueLocalMoment(at, timezone) {
    const millis = typeof at === 'number' ? at : epochMillisFromIso(at);
    const parts = venueLocalParts(millis, timezone);
    const secondOfDay = parts.hour * 3600 + parts.minute * SECONDS_PER_MINUTE + parts.second;
    return {
        date: `${pad(parts.year, 4)}-${pad(parts.month, 2)}-${pad(parts.day, 2)}`,
        time: `${pad(parts.hour, 2)}:${pad(parts.minute, 2)}`,
        secondOfDay,
        weekday: parts.weekday,
    };
}
/**
 * The instant at which a local date and time of day happens in the venue.
 *
 * A local wall time is not one instant until a timezone says so, and a timezone's offset depends
 * on the instant — so the offset is looked up at a first guess and then re-checked, which settles
 * every case except the hour that a daylight-saving jump deletes or repeats. In a deleted hour the
 * result lands on the instant the offset changed; in a repeated hour it takes the first pass. India
 * has no daylight saving, so no Indian venue can meet either case; the rule is written down for the
 * markets that can.
 */
export function instantAtVenueLocal(date, secondOfDay, timezone) {
    assertIntegerInRange(secondOfDay, 'secondOfDay', 0, SECONDS_PER_DAY - 1);
    const { year, month, day } = parseLocalDate(date);
    const asIfUtc = utcMillisFromYmd(year, month, day) + secondOfDay * MILLIS_PER_SECOND;
    const firstOffset = offsetSecondsAt(asIfUtc, timezone);
    const firstGuess = asIfUtc - firstOffset * MILLIS_PER_SECOND;
    const secondOffset = offsetSecondsAt(firstGuess, timezone);
    return secondOffset === firstOffset ? firstGuess : asIfUtc - secondOffset * MILLIS_PER_SECOND;
}
/**
 * The instant a venue's wall clock **reaches** a local date and time of day.
 *
 * The same as `instantAtVenueLocal` for every time that exists. The difference is the hour a
 * spring change deletes: `instantAtVenueLocal` reads a missing wall time with the offset in force
 * after the change, which lands it an hour *early* — 02:10 on a New York spring-forward day comes
 * back as 01:10 EST, before the clocks have moved. For a boundary that is wrong in a way that
 * matters: a business day that starts before its own date begins, a venue that "closes" an hour
 * before its owner's clock says so. So here a missing wall time happens **at the moment the clocks
 * jump over it** — the first instant whose wall clock reads that time or later — which is what a
 * person standing in the venue would say. A repeated wall time takes its first pass, as before.
 *
 * `instantAtVenueLocal` is left as it is: pass windows and their shared test vectors depend on it,
 * and changing time arithmetic under them is a separate, tested decision.
 */
export function instantWhenVenueClockReads(date, secondOfDay, timezone) {
    const candidate = instantAtVenueLocal(date, secondOfDay, timezone);
    const seen = venueLocalMoment(candidate, timezone);
    if (seen.date === date && seen.secondOfDay === secondOfDay)
        return candidate;
    // The wall time does not exist. Search for the jump: the clock reads earlier than asked at `low`
    // and at or later at `high`. No zone has ever skipped a day, so a day either side bounds it, and
    // no zone repeats an hour within a day of skipping one, so the reading is monotonic in between.
    let low = candidate - SECONDS_PER_DAY * MILLIS_PER_SECOND;
    let high = candidate + SECONDS_PER_DAY * MILLIS_PER_SECOND;
    while (high - low > MILLIS_PER_SECOND) {
        const mid = low + Math.floor((high - low) / 2 / MILLIS_PER_SECOND) * MILLIS_PER_SECOND;
        if (venueClockAtOrAfter(mid, date, secondOfDay, timezone))
            high = mid;
        else
            low = mid;
    }
    return high;
}
/** Does the venue's wall clock read `date` `secondOfDay` or later at this instant? */
function venueClockAtOrAfter(millis, date, secondOfDay, timezone) {
    const seen = venueLocalMoment(millis, timezone);
    return seen.date > date || (seen.date === date && seen.secondOfDay >= secondOfDay);
}
/** Local midnight, as an instant. The start of a venue's day. */
export function startOfVenueDay(date, timezone) {
    return instantAtVenueLocal(date, 0, timezone);
}
/**
 * The last second of a venue's day: 23:59:59 local. This is when a pass expires — "expiry is at
 * 23:59:59 venue local time of the last valid day" (spec/06 §3.2) — so the whole of the last day
 * is usable, which is what a player who bought a "7 day" pass expects.
 */
export function endOfVenueDay(date, timezone) {
    return instantAtVenueLocal(date, SECONDS_PER_DAY - 1, timezone);
}
/** The venue's calendar date `days` before or after another one. Pure calendar arithmetic. */
export function shiftLocalDate(date, days) {
    assertIntegerInRange(days, 'days', -3_650_000, 3_650_000);
    const { year, month, day } = parseLocalDate(date);
    const shifted = new Date(utcMillisFromYmd(year, month, day + days));
    return `${pad(shifted.getUTCFullYear(), 4)}-${pad(shifted.getUTCMonth() + 1, 2)}-${pad(shifted.getUTCDate(), 2)}`;
}
/** The weekday of a venue's calendar date, without going near a timezone. */
export function weekdayOfLocalDate(date) {
    const { year, month, day } = parseLocalDate(date);
    // getUTCDay is 0 = Sunday; WEEKDAYS starts at Monday.
    const index = (new Date(utcMillisFromYmd(year, month, day)).getUTCDay() + 6) % 7;
    const weekday = WEEKDAYS[index];
    if (weekday === undefined) {
        throw new DomainError('invalid-moment', `Could not place ${date} on a weekday.`, { date });
    }
    return weekday;
}
/** `HH:mm` as seconds since local midnight. */
export function secondOfDayFromLocalTime(time) {
    const match = LOCAL_TIME.exec(time);
    if (match === null) {
        throw new DomainError('invalid-moment', `Expected a time of day as HH:mm, got "${time}".`, { time });
    }
    return Number(match[1]) * 3600 + Number(match[2]) * SECONDS_PER_MINUTE;
}
/** Seconds since local midnight as `HH:mm`, for the copy that says "until 17:50". */
export function localTimeFromSecondOfDay(secondOfDay) {
    assertIntegerInRange(secondOfDay, 'secondOfDay', 0, SECONDS_PER_DAY - 1);
    const minuteOfDay = Math.floor(secondOfDay / SECONDS_PER_MINUTE);
    return `${pad(Math.floor(minuteOfDay / 60), 2)}:${pad(minuteOfDay % 60, 2)}`;
}
/** The timezone's offset from UTC at an instant, in seconds east of Greenwich. */
export function offsetSecondsAt(millis, timezone) {
    const parts = venueLocalParts(millis, timezone);
    const asIfUtc = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second);
    return Math.round((asIfUtc - Math.floor(millis / MILLIS_PER_SECOND) * MILLIS_PER_SECOND) / MILLIS_PER_SECOND);
}
const formatters = new Map();
function formatterFor(timezone) {
    const cached = formatters.get(timezone);
    if (cached !== undefined)
        return cached;
    let formatter;
    try {
        formatter = new Intl.DateTimeFormat('en-US', {
            timeZone: timezone,
            hourCycle: 'h23',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            weekday: 'short',
        });
    }
    catch {
        throw new DomainError('invalid-timezone', `"${timezone}" is not an IANA timezone name.`, { timezone });
    }
    formatters.set(timezone, formatter);
    return formatter;
}
const WEEKDAY_BY_LABEL = {
    Mon: 'mon',
    Tue: 'tue',
    Wed: 'wed',
    Thu: 'thu',
    Fri: 'fri',
    Sat: 'sat',
    Sun: 'sun',
};
function venueLocalParts(millis, timezone) {
    assertIntegerInRange(Math.trunc(millis), 'at', -8.64e15, 8.64e15);
    const parts = formatterFor(timezone).formatToParts(new Date(millis));
    const found = {};
    for (const part of parts) {
        if (part.type !== 'literal')
            found[part.type] = part.value;
    }
    const weekday = WEEKDAY_BY_LABEL[found['weekday'] ?? ''];
    if (weekday === undefined) {
        throw new DomainError('invalid-timezone', `Could not read a weekday in "${timezone}".`, { timezone });
    }
    return {
        year: Number(found['year']),
        month: Number(found['month']),
        day: Number(found['day']),
        hour: Number(found['hour']),
        minute: Number(found['minute']),
        second: Number(found['second']),
        weekday,
    };
}
function parseLocalDate(date) {
    const match = LOCAL_DATE.exec(date);
    if (match === null) {
        throw new DomainError('invalid-moment', `Expected a venue-local date as YYYY-MM-DD, got "${date}".`, { date });
    }
    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    if (month < 1 || month > 12 || day < 1 || day > 31) {
        throw new DomainError('invalid-moment', `"${date}" is not a real date.`, { date });
    }
    return { year, month, day };
}
function pad(value, width) {
    return String(value).padStart(width, '0');
}
/** `Date.UTC` maps a two-digit year onto the 1900s; venues have four-digit years, so be explicit. */
function utcMillisFromYmd(year, month, day) {
    const millis = Date.UTC(year, month - 1, day);
    if (year >= 0 && year < 100) {
        const corrected = new Date(millis);
        corrected.setUTCFullYear(year);
        return corrected.getTime();
    }
    return millis;
}
//# sourceMappingURL=venue-time.js.map